import {
  Component, ChangeDetectionStrategy, Input, OnInit, OnChanges, SimpleChanges, inject,
} from '@angular/core';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { PdksDurumService } from '@uygulamalar/hmi-pano/services/pdks-durum.service';
import type { IAylikOzet, IAylikGunOzet, PuantajGunDurum } from '@uygulamalar/firma-yonetim/interfaces/pdks.interface';

const AY_ISIMLERI = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

// ─── Renk kodları ────────────────────────────────────────────────────────────
const DURUM_RENK: Record<PuantajGunDurum, string> = {
  normal: '#52c41a',
  gec_geldi: '#faad14',
  devamsiz: '#ff4d4f',
  izinli: '#1890ff',
  raporlu: '#1890ff',
  tatil: '#722ed1',
  calisma_gunu_degil: '#d9d9d9',
  mesaili: '#fa8c16',
  erken_cikti: '#faad14',
  gece_vardiyasi: '#1890ff',
};

@Component({
  selector: 'pdks-aylik-ozet',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, NzButtonModule],
  templateUrl: './pdks-aylik-ozet.component.html',
  styleUrl: './pdks-aylik-ozet.component.less',
})
export class PdksAylikOzetComponent extends FikoYTKokBilesenComponent implements OnInit, OnChanges {
  constructor() { super(); }
  private pdks = inject(PdksDurumService);

  @Input() firmaId = '';
  @Input() personelId = '';

  readonly AY = AY_ISIMLERI;
  yil = new Date().getFullYear();
  ay = new Date().getMonth();
  ozet: IAylikOzet | null = null;
  yukleniyor = false;

  ngOnInit() { this.yukle(); }
  ngOnChanges(c: SimpleChanges) { if (c['personelId'] && !c['personelId'].firstChange) this.yukle(); }

  async yukle() {
    this.yukleniyor = true; this.CD.detectChanges();
    try {
      this.ozet = await this.pdks.aylikOzetHesapla(this.yil, this.ay);
    } catch { this.nzMessage.error('Aylık özet yüklenemedi'); }
    finally { this.yukleniyor = false; this.CD.detectChanges(); }
  }

  async oncekiAy() { if (this.ay === 0) { this.ay = 11; this.yil--; } else this.ay--; await this.yukle(); }
  async sonrakiAy() { if (this.ay === 11) { this.ay = 0; this.yil++; } else this.ay++; await this.yukle(); }

  gunNo(tarih: string): number { return parseInt(tarih.split('-')[2]); }
  gunIsim(tarih: string): string {
    const gun = new Date(tarih).getDay();
    return ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'][gun];
  }

  ilkGiris(g: IAylikGunOzet): string {
    const ilk = g.kayitlar.find(k => k.giris);
    return ilk?.giris ? new Date(ilk.giris).toTimeString().substring(0, 5) : '—';
  }

  sonCikis(g: IAylikGunOzet): string {
    const son = [...g.kayitlar].reverse().find(k => k.cikis);
    return son?.cikis ? new Date(son.cikis).toTimeString().substring(0, 5) : '—';
  }

  durumRenk(d: PuantajGunDurum): string { return DURUM_RENK[d] || '#999'; }
  durumEtiket(d: PuantajGunDurum): string {
    const m: Record<string, string> = {
      normal: 'Normal', gec_geldi: 'Geç', devamsiz: 'Devamsız', izinli: 'İzinli',
      raporlu: 'Raporlu', tatil: 'Tatil', calisma_gunu_degil: 'Dinlenme',
      mesaili: 'Mesaili', erken_cikti: 'Erken', gece_vardiyasi: 'Gece',
    };
    return m[d] || d;
  }

  dk2Str(dk: number): string {
    if (dk <= 0) return '0dk';
    const h = Math.floor(dk / 60); const m = dk % 60;
    return h > 0 ? `${h}s ${m}dk` : `${m}dk`;
  }

  // ─── CSV Export ────────────────────────────────────────────────────────────
  csvIndir() {
    if (!this.ozet) return;
    const baslik = 'Gün,Durum,Kayıt Sayısı,Giriş,Çıkış,Mola(dk),Net(dk),F.Mesai(dk),Çarpan\n';
    const satirlar = this.ozet.gunler.map(g => {
      return `${g.tarih},${this.durumEtiket(g.durum)},${g.kayitlar.length},${this.ilkGiris(g)},${this.sonCikis(g)},${g.toplamMolaDk},${g.toplamCalismaDk},${g.fazlaMesaiDk},${g.mesaiCarpani}`;
    }).join('\n');
    const blob = new Blob(['\ufeff' + baslik + satirlar], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `puantaj_${this.yil}_${String(this.ay + 1).padStart(2, '0')}.csv`;
    a.click(); URL.revokeObjectURL(url);
    this.nzMessage.success('CSV indirildi');
  }

  // ─── Print ─────────────────────────────────────────────────────────────────
  yazdir() { window.print(); }
}
